/**
 * A person's app preferences (M8-2, D196): language and light / dark / as-the-device. Kept on the
 * server so they follow the person to another phone; changed on a phone through the sync outbox
 * (`user.preferences.set`), so a change made without internet arrives later, once. The latest
 * change wins, by the time the person made it on the phone — never by arrival order.
 */
import { z } from 'zod';

export const APP_LANGUAGES = ['ar', 'en'] as const;
export type AppLanguage = (typeof APP_LANGUAGES)[number];

export const APP_THEMES = ['system', 'light', 'dark'] as const;
export type AppTheme = (typeof APP_THEMES)[number];

/** Sync command type (apps/api/src/users/preferences.ts). */
export const USER_PREFERENCES_SET = 'user.preferences.set';

export const UserPreferencesPayloadSchema = z
  .object({
    lang: z.enum(APP_LANGUAGES).optional(),
    theme: z.enum(APP_THEMES).optional(),
  })
  .strict()
  .refine((p) => p.lang !== undefined || p.theme !== undefined, {
    message: 'nothing_to_change',
  });
export type UserPreferencesPayload = z.infer<typeof UserPreferencesPayloadSchema>;

/**
 * Whether a change made at `changedAt` replaces what the server holds (made at `heldAt`, null when
 * never changed through the app). Equal times keep what is held (the first one to arrive).
 */
export function preferenceChangeWins(changedAt: string, heldAt: string | null): boolean {
  return heldAt === null || Date.parse(changedAt) > Date.parse(heldAt);
}
